import { RowCrudStatus } from "../../common/types";
import {
  Tenant,
  TenantCreateMutationVariables,
  TenantUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { tenantTypeDisplayId } from "../../tenantType/store/TenantTypeCommonStore";
import { tenantStatusDisplayId } from "../../tenantStatus/store/TenantStatusCommonStore";
import { tenantPlanDisplayId } from "../../tenantPlan/store/TenantPlanCommonStore";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { getFileVariableValue } from "../../common/utils";
import { blankToNull } from "../../../common/blankToNull";
import { llmProviderDisplayId } from "../../llmProvider/store/LlmProviderCommonStore";
import { EsignatureProvider } from "../../../api/graphql";

let uniqueCounter = 1;
export type TenantValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name: string;
  __tenantType: string;
  showTenantType: { displayId: string; id: string } | null;
  __status: string;
  showStatus: { displayId: string; id: string } | null;
  __plan: string;
  showPlan: { displayId: string; id: string } | null;
  logo: string;
  logo_data: FileCRUDType;
  __logo_files: FileCRUDType[];
  publicUrl: string;
  idERP: string;
  idCRM: string;
  monthlyTokenLimit: number | undefined;
  currentMonthTokens: number;
  tokenLimitResetDay: number;
  lastTokenResetDate: Date | null;
  __ragLlmProvider: string;
  showRagLlmProvider: { displayId: string; id: string } | null;
  ragLlmModel: string;
  currentStorageBytes: number;
  storageByteLimit: number | undefined;
  eSignatureProvider: EsignatureProvider | "";
};

export type TenantErrorsType = {
  name?: { message: string; fieldLabel: string };
  tenantType?: { message: string; fieldLabel: string };
  status?: { message: string; fieldLabel: string };
  plan?: { message: string; fieldLabel: string };
  logo?: { message: string; fieldLabel: string };
  publicUrl?: { message: string; fieldLabel: string };
  idERP?: { message: string; fieldLabel: string };
  idCRM?: { message: string; fieldLabel: string };
  monthlyTokenLimit?: { message: string; fieldLabel: string };
  currentMonthTokens?: { message: string; fieldLabel: string };
  tokenLimitResetDay?: { message: string; fieldLabel: string };
  lastTokenResetDate?: { message: string; fieldLabel: string };
  ragLlmProvider?: { message: string; fieldLabel: string };
  ragLlmModel?: { message: string; fieldLabel: string };
  currentStorageBytes?: { message: string; fieldLabel: string };
  storageByteLimit?: { message: string; fieldLabel: string };
  eSignatureProvider?: { message: string; fieldLabel: string };
};

export function TenantValuesInit(): TenantValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name: "",
    __tenantType: "",
    showTenantType: null,
    __status: "",
    showStatus: null,
    __plan: "",
    showPlan: null,
    logo: "",
    logo_data: fileCRUDInit(),
    __logo_files: [],
    publicUrl: "",
    idERP: "",
    idCRM: "",
    monthlyTokenLimit: undefined,
    currentMonthTokens: 0,
    tokenLimitResetDay: 1,
    lastTokenResetDate: null,
    __ragLlmProvider: "",
    showRagLlmProvider: null,
    ragLlmModel: "",
    currentStorageBytes: 0,
    storageByteLimit: undefined,
    eSignatureProvider: "" as EsignatureProvider | "",
  };
}

export type TenantType = Tenant | TenantValuesType;

export function tenantDisplayId(
  source: Partial<Tenant>,
  _langStore: LangStore
) {
  return source.name ?? _langStore.emptyStringSymbol;
}

export function getTenantValues(
  source: TenantType,
  _langStore: LangStore,
  _duplicated: boolean = false
): TenantValuesType {
  const entityValues = TenantValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : tenantDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name = source.name;
  entityValues.showTenantType =
    "tenantType" in source && source.tenantType
      ? {
          id: source.tenantType.id,
          displayId: tenantTypeDisplayId(source.tenantType, _langStore),
        }
      : "showTenantType" in source && source.showTenantType
      ? {
          id: source.showTenantType.id,
          displayId: source.showTenantType.displayId,
        }
      : null;
  entityValues.__tenantType = entityValues.showTenantType?.displayId || "";
  entityValues.showStatus =
    "status" in source && source.status
      ? {
          id: source.status.id,
          displayId: tenantStatusDisplayId(source.status, _langStore),
        }
      : "showStatus" in source && source.showStatus
      ? { id: source.showStatus.id, displayId: source.showStatus.displayId }
      : null;
  entityValues.__status = entityValues.showStatus?.displayId || "";
  entityValues.showPlan =
    "plan" in source && source.plan
      ? {
          id: source.plan.id,
          displayId: tenantPlanDisplayId(source.plan, _langStore),
        }
      : "showPlan" in source && source.showPlan
      ? { id: source.showPlan.id, displayId: source.showPlan.displayId }
      : null;
  entityValues.__plan = entityValues.showPlan?.displayId || "";
  entityValues.__logo_files =
    source.logo_data && !_duplicated
      ? [
          {
            id:
              "id" in source.logo_data && source.logo_data.id
                ? source.logo_data.id
                : `generated_${uniqueCounter++}`,
            name: source.logo_data.name,
            description: source.logo_data.description || undefined,
            size: source.logo_data.size || 0,
            formattedSize: source.logo_data.size
              ? formatBytes(source.logo_data.size)
              : "",
            mimetype: source.logo_data.mimetype,
            url: source.logo_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.logo = source.logo && !_duplicated ? source.logo : "";
  entityValues.logo_data =
    source.logo_data && "__status__" in source.logo_data
      ? source.logo_data
      : source.logo_data
      ? {
          id: ("id" in source.logo_data && source.logo_data.id
            ? source.logo_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.logo_data.name,
          description: source.logo_data.description || undefined,
          size: source.logo_data.size || 0,
          formattedSize: source.logo_data.size
            ? formatBytes(source.logo_data.size)
            : "",
          mimetype: source.logo_data.mimetype,
          url: source.logo_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.publicUrl = source.publicUrl || "";
  entityValues.idERP = source.idERP || "";
  entityValues.idCRM = source.idCRM || "";
  entityValues.monthlyTokenLimit =
    (source.monthlyTokenLimit as unknown) === "" ||
    source.monthlyTokenLimit === undefined ||
    source.monthlyTokenLimit === null
      ? undefined
      : source.monthlyTokenLimit;
  entityValues.currentMonthTokens = source.currentMonthTokens || 0;
  entityValues.tokenLimitResetDay = source.tokenLimitResetDay || 0;
  entityValues.lastTokenResetDate = source.lastTokenResetDate
    ? new Date(source.lastTokenResetDate)
    : null;
  entityValues.showRagLlmProvider =
    "ragLlmProvider" in source && source.ragLlmProvider
      ? {
          id: source.ragLlmProvider.id,
          displayId: llmProviderDisplayId(source.ragLlmProvider, _langStore),
        }
      : "showRagLlmProvider" in source && source.showRagLlmProvider
      ? {
          id: source.showRagLlmProvider.id,
          displayId: source.showRagLlmProvider.displayId,
        }
      : null;
  entityValues.__ragLlmProvider =
    entityValues.showRagLlmProvider?.displayId || "";
  entityValues.ragLlmModel = source.ragLlmModel || "";
  entityValues.currentStorageBytes = source.currentStorageBytes || 0;
  entityValues.storageByteLimit =
    (source.storageByteLimit as unknown) === "" ||
    source.storageByteLimit === undefined ||
    source.storageByteLimit === null
      ? undefined
      : source.storageByteLimit;
  entityValues.eSignatureProvider =
    source.eSignatureProvider || ("" as EsignatureProvider | "");
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getTenantUpdateVariables(
  values: TenantValuesType
): TenantUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name: values.name,
    tenantTypeId: values.showTenantType?.id || "",
    statusId: values.showStatus?.id || "",
    planId: values.showPlan?.id || "",
    logo: getFileVariableValue(values.__logo_files),
    publicUrl: blankToNull(values.publicUrl),
    idERP: blankToNull(values.idERP),
    idCRM: blankToNull(values.idCRM),
    monthlyTokenLimit:
      values.monthlyTokenLimit === undefined ? null : values.monthlyTokenLimit,
    currentMonthTokens: values.currentMonthTokens,
    tokenLimitResetDay: values.tokenLimitResetDay,
    lastTokenResetDate: values.lastTokenResetDate?.toISOString() || null,
    ragLlmProviderId: values.showRagLlmProvider?.id || "",
    ragLlmModel: blankToNull(values.ragLlmModel),
    currentStorageBytes: values.currentStorageBytes,
    storageByteLimit:
      values.storageByteLimit === undefined ? null : values.storageByteLimit,
    eSignatureProvider: values.eSignatureProvider || null,
  };
}

export function getTenantCreateVariables(
  values: TenantValuesType
): TenantCreateMutationVariables {
  return {
    name: values.name,
    tenantTypeId: values.showTenantType?.id || "",
    statusId: values.showStatus?.id || "",
    planId: values.showPlan?.id || "",
    logo: getFileVariableValue(values.__logo_files),
    publicUrl: blankToNull(values.publicUrl),
    idERP: blankToNull(values.idERP),
    idCRM: blankToNull(values.idCRM),
    monthlyTokenLimit:
      values.monthlyTokenLimit === undefined ? null : values.monthlyTokenLimit,
    currentMonthTokens: values.currentMonthTokens,
    tokenLimitResetDay: values.tokenLimitResetDay,
    lastTokenResetDate: values.lastTokenResetDate?.toISOString() || null,
    ragLlmProviderId: values.showRagLlmProvider?.id || "",
    ragLlmModel: blankToNull(values.ragLlmModel),
    currentStorageBytes: values.currentStorageBytes,
    storageByteLimit:
      values.storageByteLimit === undefined ? null : values.storageByteLimit,
    eSignatureProvider: values.eSignatureProvider || null,
  };
}
